
import { ClienteSubdivisaoRegra, DesmembramentoResult } from '@/types/clienteSubdivisao';
import { buscarRegrasSubdivisao } from './buscarRegras';
import { supabase } from '@/integrations/supabase/client';

export type AplicarRegrasDesmembramentoOpcoes = {
  /** Em reprocessamento em lote, evita uma linha de auditoria por OS (a atualização da OS já registra o efeito). */
  skipAuditoria?: boolean;
  /**
   * Quando informado, evita N consultas: mapa cliente_pai_id → regras (mesma ordem de `buscarRegrasSubdivisao`).
   */
  regrasCache?: Map<number, ClienteSubdivisaoRegra[]>;
  /**
   * Horas usadas na condição de limite (ex.: somatório mensal do bucket). Se retornar `undefined`, usa `horasTotais`.
   */
  horasPorRegra?: (regra: ClienteSubdivisaoRegra) => number | undefined;
  /**
   * Para `operador_logico` `<`, usa `<= limite` em vez de `< limite` (faixas tipo ≤360h / >360h).
   */
  condicaoLimiteMenorInclusive?: boolean;
};

export const aplicarRegrasDesmembramento = async (
  clienteOriginalId: number,
  operadorId: number | null,
  produto: string | null,
  tipoProjeto: string,
  horasTotais: number,
  numeroOS: string,
  opcoes?: AplicarRegrasDesmembramentoOpcoes
): Promise<DesmembramentoResult> => {
  const loud = !opcoes?.skipAuditoria;
  if (loud) {
    console.log(`=== APLICANDO REGRAS DE DESMEMBRAMENTO PARA OS ${numeroOS} ===`);
    console.log('Dados da OS:', {
      clienteOriginalId,
      operadorId,
      produto,
      tipoProjeto,
      horasTotais,
    });
  }

  try {
    // Buscar regras para o cliente pai
    const regras =
      opcoes?.regrasCache?.get(clienteOriginalId) ?? (await buscarRegrasSubdivisao(clienteOriginalId));
    
    if (regras.length === 0) {
      if (loud) console.log(`Nenhuma regra encontrada para cliente ${clienteOriginalId}`);
      return {
        clienteRedirecionado: null,
        regraAplicada: null,
        justificativa: null
      };
    }

    if (loud) console.log(`Encontradas ${regras.length} regras para cliente ${clienteOriginalId}`);

    // Verificar cada regra por ordem de prioridade
    for (const regra of regras) {
      if (loud) console.log(`🔍 Verificando regra ${regra.id} (prioridade: ${regra.prioridade})`);
      
      let regraAplicavel = true;
      let justificativa = [`Regra ${regra.id} (prioridade ${regra.prioridade})`];

      // 1. Verificar operador (se definido na regra)
      if (regra.operador_ids && regra.operador_ids.length > 0) {
        if (!operadorId || !regra.operador_ids.includes(operadorId)) {
          if (loud) console.log(`❌ Regra ${regra.id}: Operador ${operadorId} não está na lista da regra`);
          continue;
        }
        justificativa.push(`Operador ${operadorId} está na lista permitida`);
      }

      // 2. Verificar produto (se definido na regra)
      if (regra.produto) {
        if (!produto || produto.toUpperCase() !== regra.produto.toUpperCase()) {
          if (loud) console.log(`❌ Regra ${regra.id}: Produto "${produto}" ≠ "${regra.produto}"`);
          continue;
        }
        justificativa.push(`Produto "${produto}" corresponde ao requerido "${regra.produto}"`);
      }

      // 3. Verificar tipo de projeto
      if (tipoProjeto.toUpperCase() !== regra.tipo_projeto) {
        if (loud) console.log(`❌ Regra ${regra.id}: Tipo projeto "${tipoProjeto}" ≠ "${regra.tipo_projeto}"`);
        continue;
      }
      justificativa.push(`Tipo de projeto "${tipoProjeto}" corresponde ao requerido "${regra.tipo_projeto}"`);

      // 4. Verificar condição de horas
      const horasCondicaoBruto = opcoes?.horasPorRegra?.(regra);
      const horasCondicao = horasCondicaoBruto !== undefined ? horasCondicaoBruto : horasTotais;
      const limite = Number(regra.quantidade_horas_limite);
      const condicaoAtendida =
        regra.operador_logico === '>'
          ? horasCondicao > limite
          : opcoes?.condicaoLimiteMenorInclusive
            ? horasCondicao <= limite
            : horasCondicao < limite;

      if (!condicaoAtendida) {
        if (loud)
          console.log(
            `[regra ${regra.id}] Condição de horas não atendida (${horasCondicao} ${regra.operador_logico} ${limite})`
          );
        continue;
      }
      justificativa.push(`Horas ${horasCondicao} ${regra.operador_logico} ${limite} (condição atendida)`);

      // Se chegou aqui, a regra é aplicável
      justificativa.push(`Cliente redirecionado para ID ${regra.cliente_filho_id}`);
      if (regra.valor_hora) {
        justificativa.push(`Valor/hora aplicado: R$ ${regra.valor_hora}`);
      }

      const justificativaCompleta = justificativa.join(' | ');

      // Registrar na auditoria (importação/sync); reprocessamento em lote pode omitir
      if (!opcoes?.skipAuditoria) {
        try {
          await supabase
            .from('cliente_subdivisao_auditoria')
            .insert({
              ordem_servico_numero: numeroOS,
              cliente_original_id: clienteOriginalId,
              cliente_redirecionado_id: regra.cliente_filho_id,
              regra_id: regra.id,
              justificativa: justificativaCompleta
            });
        } catch (auditError) {
          console.error('Erro ao registrar auditoria:', auditError);
        }
      }

      if (loud) {
        console.log(`✅ OS ${numeroOS} redirecionada de cliente ${clienteOriginalId} para ${regra.cliente_filho_id}`);
        console.log(`📋 Justificativa: ${justificativaCompleta}`);
      }

      return {
        clienteRedirecionado: regra.cliente_filho_id,
        regraAplicada: regra,
        justificativa: justificativaCompleta
      };
    }

    if (loud) console.log(`Nenhuma regra aplicável encontrada para OS ${numeroOS}`);
    return {
      clienteRedirecionado: null,
      regraAplicada: null,
      justificativa: null
    };

  } catch (error) {
    console.error(`Erro ao aplicar regras de desmembramento para OS ${numeroOS}:`, error);
    return {
      clienteRedirecionado: null,
      regraAplicada: null,
      justificativa: null
    };
  }
};
